/**
 * Hierarchy Schema Definitions
 *
 * Declarative bootstrap tree (accounts → orgs → workspaces, plus
 * channels/providers/connections per org) as applied by bootstrapHierarchy,
 * and the split platform / workspace-connector shapes used for HTTP seeding.
 * Connection refs are raw UUIDs that must already appear above them in the
 * same tree.
 */

import { z } from "zod";
import { MetadataField } from "./common";
import { schemas as accountSchemas } from "./account-schemas";

const HierarchyChannelSpec = z.object({
  id: z.uuid(),
  name: z.string().min(1).max(200),
  type: z.string().min(1).max(100),
});

const HierarchyProviderSpec = z.object({
  id: z.uuid(),
  name: z.string().min(1).max(200),
  type: z.string().min(1).max(100),
});

const HierarchyConnectionSpec = z.object({
  id: z.uuid(),
  workspaceId: z.uuid(),
  channelId: z.uuid(),
  providerId: z.uuid(),
  name: z.string().min(1).max(200),
});

const HierarchyWorkspaceSpec = z.object({
  id: z.uuid(),
  name: z.string().min(1).max(200),
  key: z.string().min(1).max(200).optional(),
  metadata: MetadataField,
});

const HierarchyOrganizationSpec = z.object({
  id: z.uuid(),
  slug: accountSchemas.OrganizationSlugField,
  name: z.string().min(1).max(200),
  metadata: MetadataField,
  workspaces: z.array(HierarchyWorkspaceSpec),
  channels: z.array(HierarchyChannelSpec),
  providers: z.array(HierarchyProviderSpec),
  connections: z.array(HierarchyConnectionSpec),
});

const HierarchyAccountSpec = z.object({
  id: z.uuid(),
  name: z.string().min(1).max(200),
  email: z.email().optional(),
  metadata: MetadataField,
  organizations: z.array(HierarchyOrganizationSpec),
});

const HierarchyTree = z.object({
  accounts: z.array(HierarchyAccountSpec),
});

// ── Split shapes for HTTP transport ────────────────────────────────────────
// Platform: account/org/workspace rows (global platform DO).
// Workspace connectors: channels/providers/connections (caller's workspace DO).

const PlatformWorkspaceSpec = HierarchyWorkspaceSpec;

const PlatformOrganizationSpec = HierarchyOrganizationSpec.omit({
  channels: true,
  providers: true,
  connections: true,
});

const PlatformAccountSpec = HierarchyAccountSpec.extend({
  organizations: z.array(PlatformOrganizationSpec),
});

const PlatformTree = HierarchyTree.extend({
  accounts: z.array(PlatformAccountSpec),
});

const WorkspaceChannelSpec = HierarchyChannelSpec.extend({
  organizationId: z.uuid(),
});

const WorkspaceProviderSpec = HierarchyProviderSpec.extend({
  organizationId: z.uuid(),
});

const WorkspaceConnectionSpec = HierarchyConnectionSpec;

const WorkspaceConnectors = z.object({
  channels: z.array(WorkspaceChannelSpec),
  providers: z.array(WorkspaceProviderSpec),
  connections: z.array(WorkspaceConnectionSpec),
});

// Export all schemas
export const schemas = {
  HierarchyChannelSpec,
  HierarchyProviderSpec,
  HierarchyConnectionSpec,
  HierarchyWorkspaceSpec,
  HierarchyOrganizationSpec,
  HierarchyAccountSpec,
  HierarchyTree,
  PlatformWorkspaceSpec,
  PlatformOrganizationSpec,
  PlatformAccountSpec,
  PlatformTree,
  WorkspaceChannelSpec,
  WorkspaceProviderSpec,
  WorkspaceConnectionSpec,
  WorkspaceConnectors,
};

// oRPC Type Inference Support
export type SchemaTypes = {
  [K in keyof typeof schemas]: z.infer<(typeof schemas)[K]>;
};
